import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { scansApi } from '../api/scans.ts';
import { authApi } from '../api/auth.ts';
import { ApiError } from '../api/client.ts';
import { useAuthStore } from '../state/authStore.ts';
import { storeAnonymousScanToken } from '../state/anonymousScans.ts';
import { useSeo } from '../hooks/useSeo.ts';
import TechStackBar from '../components/TechStackBar.tsx';

// FAQ content. Answer strings must match the visible DOM text 1:1 — Google's FAQPage
// rich-result eligibility revokes if schema and rendered content drift.
const FAQ_ITEMS: ReadonlyArray<{ q: string; a: string }> = [
  {
    q: 'Who has to comply with the European Accessibility Act?',
    a: 'The EAA (Directive 2019/882) applies to businesses selling digital products or services to EU consumers as of 28 June 2025. That covers e-commerce, banking, e-books, ticketing, transport, and most consumer-facing web apps. Companies with fewer than 10 employees and under €2M annual turnover are exempt for services, but not for physical products.',
  },
  {
    q: 'What does an EAA Proof scan actually do?',
    a: 'We load your URL in a real Chromium browser, crawl up to 8 same-origin pages via your sitemap and in-page link graph, and run axe-core 4.x — the same WCAG 2.1 AA engine used by Microsoft, Google, and the BBC. Every violation comes back with a plain-English explanation, a code-level remediation, and a citation to the relevant WCAG success criterion.',
  },
  {
    q: 'How long does a scan take?',
    a: 'About 60 seconds for a typical site. When it finishes you get a downloadable PDF audit report and a ready-to-publish accessibility statement HTML, both in your account.',
  },
  {
    q: 'Does this replace a manual accessibility audit?',
    a: 'No — and we will not pretend it does. Automated tools like axe-core catch roughly 30–40% of WCAG issues: the deterministic ones (missing alt text, low contrast, broken labels, ARIA misuse). Cognitive load, keyboard-only flows, and screen-reader UX still need a human. EAA Proof is an affordable automated baseline; a full manual audit is the gold standard.',
  },
  {
    q: 'Is the accessibility statement based on the official EU template?',
    a: 'Yes. The statement follows the European Commission model accessibility statement template (Implementing Decision (EU) 2018/1523) issued under Directive 2016/2102, which the EAA inherits for digital services. You fill in your enforcement contact and review date — the template guides you. We use the same statement on eaaproof.com itself. It is a draft you should review and adapt before publishing.',
  },
  {
    q: 'Can the AI hallucinate violations?',
    a: 'No. Detection is done by axe-core, which is deterministic — the AI never invents a violation. We only use Claude to write the plain-English explanation, and every AI output passes grounding checks against the actual HTML found on your page. If a check fails the violation still ships, just with raw axe-core data and no AI gloss. We never write a fabricated fix into a regulator-facing PDF.',
  },
  {
    q: 'What does it cost?',
    a: 'A one-off scan is €29. Basic is €9/month for 5 scans. Agency is €49/month for 50 scans with your branding on the PDF. EU VAT is calculated automatically, B2B reverse-charge is supported, and you can cancel any subscription in two clicks via the Stripe Customer Portal.',
  },
];

// FAQPage JSON-LD. The acceptedAnswer.text must match the visible answer above so the rich
// result stays eligible — keep this in lockstep with FAQ_ITEMS if you edit either.
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

function MockReportPreview() {
  return (
    <div className="mock-report" aria-label="Example report preview" aria-hidden="true">
      <div className="mock-report-header">
        <span className="mock-report-brand">EAA Proof</span>
        <span className="mock-report-subtitle">WCAG 2.1 AA Audit</span>
        <span className="mock-report-example-tag">Example</span>
      </div>

      <div className="mock-report-score-row">
        <div className="mock-report-score">
          <div className="mock-report-score-ring">
            <span className="mock-report-score-num">82</span>
            <span className="mock-report-score-denom">/100</span>
          </div>
          <span className="mock-report-score-label">Compliance score</span>
        </div>
        <div className="mock-report-chips">
          <span className="pv-chip pv-chip-critical">3 critical</span>
          <span className="pv-chip pv-chip-serious">5 serious</span>
          <span className="pv-chip pv-chip-moderate">4 moderate</span>
          <span className="pv-chip pv-chip-minor">1 minor</span>
        </div>
      </div>

      <div className="mock-report-violations">
        <div className="mock-violation mock-violation-critical">
          <div className="mock-violation-head">
            <span className="mock-v-chip mock-v-chip-critical">Critical</span>
            <code className="mock-v-rule">color-contrast</code>
          </div>
          <p className="mock-v-desc">Text has insufficient contrast ratio (2.4:1). WCAG 1.4.3 requires 4.5:1 for normal text.</p>
          <div className="mock-v-diff">
            <div className="mock-diff-before"><span className="mock-diff-label">Before</span><code>color: #9ca3af</code></div>
            <div className="mock-diff-after"><span className="mock-diff-label">After</span><code>color: #374151</code></div>
          </div>
        </div>

        <div className="mock-violation mock-violation-serious">
          <div className="mock-violation-head">
            <span className="mock-v-chip mock-v-chip-serious">Serious</span>
            <code className="mock-v-rule">image-alt</code>
          </div>
          <p className="mock-v-desc">Image element is missing an alt attribute. Screen readers cannot describe this image to users.</p>
          <div className="mock-v-diff">
            <div className="mock-diff-before"><span className="mock-diff-label">Before</span><code>{'<img src="logo.png">'}</code></div>
            <div className="mock-diff-after"><span className="mock-diff-label">After</span><code>{'<img src="logo.png" alt="...">'}</code></div>
          </div>
        </div>

        <div className="mock-violation mock-violation-moderate">
          <div className="mock-violation-head">
            <span className="mock-v-chip mock-v-chip-moderate">Moderate</span>
            <code className="mock-v-rule">label</code>
          </div>
          <p className="mock-v-desc">Form input has no associated label element. WCAG 1.3.1 requires all inputs to be programmatically labelled.</p>
          <div className="mock-v-diff">
            <div className="mock-diff-before"><span className="mock-diff-label">Before</span><code>{'<input type="email">'}</code></div>
            <div className="mock-diff-after"><span className="mock-diff-label">After</span><code>{'<label for="e">Email</label>'}</code></div>
          </div>
        </div>
      </div>

      <div className="mock-report-footer">
        <span>Page 1 of 8</span>
        <span>EN 301 549 · WCAG 2.1 AA</span>
      </div>
    </div>
  );
}

function ScanForm({
  url,
  setUrl,
  email,
  setEmail,
  marketingConsent,
  setMarketingConsent,
  onSubmit,
  isPending,
  isOutOfCredits,
  user,
  dark,
}: {
  url: string;
  setUrl: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  marketingConsent: boolean;
  setMarketingConsent: (v: boolean) => void;
  onSubmit: (e: FormEvent) => void;
  isPending: boolean;
  isOutOfCredits: boolean;
  user: { creditBalance: number } | null;
  dark?: boolean;
}) {
  // Anonymous visitors see the optional email row directly under the URL — this lets them
  // skip the in-page email gate by giving us their address upfront. Signed-in users skip
  // the email row entirely (we already have their email on file).
  const showEmailRow = !user;
  return (
    <form
      className={dark ? 'scan-form scan-form-dark' : 'scan-form'}
      onSubmit={onSubmit}
      aria-labelledby={dark ? 'cta-scan-form-label' : 'scan-form-label'}
    >
      <div className="scan-form-row">
        <label htmlFor={dark ? 'cta-scan-url' : 'scan-url'} className="sr-only" id={dark ? 'cta-scan-form-label' : 'scan-form-label'}>
          Website URL to scan
        </label>
        <input
          id={dark ? 'cta-scan-url' : 'scan-url'}
          type="url"
          placeholder="https://your-site.com"
          value={url}
          onChange={e => setUrl(e.target.value)}
          required
          disabled={isPending}
          autoComplete="url"
          inputMode="url"
        />
        <button type="submit" disabled={isPending || !url.trim() || isOutOfCredits}>
          {isPending ? 'Scanning…' : (user ? 'Run scan' : 'Run free scan')}
        </button>
      </div>

      {showEmailRow && (
        <>
          <div className="scan-form-email-row">
            <label htmlFor={dark ? 'cta-scan-email' : 'scan-email'} className="sr-only">
              Email address (optional)
            </label>
            <input
              id={dark ? 'cta-scan-email' : 'scan-email'}
              type="email"
              placeholder="you@yourcompany.com (optional — we'll email you the PDF)"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isPending}
              autoComplete="email"
              inputMode="email"
            />
          </div>
          <label className="scan-form-consent">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={e => setMarketingConsent(e.target.checked)}
              disabled={isPending}
            />
            <span>
              Email me one follow-up if EU Accessibility Act enforcement rules change for sites
              like this one. No newsletter.
            </span>
          </label>
        </>
      )}
    </form>
  );
}

export default function LandingPage() {
  useSeo({
    title: 'EU Accessibility Act audit in 60 seconds',
    canonicalPath: '/',
  });

  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const setUser = useAuthStore(s => s.setUser);
  const [url, setUrl] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  // Hybrid email-collection: the homepage form's email field is optional. Visitors can:
  //   - Type email + start scan → upfront-email path, report mailed automatically on done
  //   - Skip email + start scan → results-then-email path, in-page gate after completion
  // The CTA form (further down the page) reuses the same fields so a visitor who scrolled
  // past the hero doesn't lose their typed email.
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);

  const isOutOfCredits = user !== null && user.creditBalance <= 0;

  // Two scan-create paths share the same UI:
  //   - Signed-in user → POST /api/scans (debits a credit, returns scanId)
  //   - Anonymous visitor → POST /api/scans/anonymous (no credit, rate-limited per IP,
  //     returns scanId + accessToken). The token lets the browser poll for status, gate the
  //     PDF behind an email, and download the report. Stored in localStorage.
  // The mutation auto-routes based on auth state at submit time.
  const mutation = useMutation({
    mutationFn: async (target: string) => {
      if (user) {
        const owned = await scansApi.create(target);
        return { ...owned, accessToken: undefined as string | undefined };
      }
      const trimmedEmail = email.trim();
      const anon = await scansApi.createAnonymous(target, {
        email: trimmedEmail || undefined,
        marketingConsent: trimmedEmail ? marketingConsent : false,
      });
      storeAnonymousScanToken(anon.scanId, anon.accessToken);
      return anon;
    },
    onSuccess: async result => {
      if (user) {
        // The scan just debited credits server-side. Refresh the auth state so the NavBar's
        // credit badge reflects the new balance instead of the stale pre-scan number.
        try {
          const refreshed = await authApi.me();
          setUser(refreshed);
        } catch {
          // Don't block navigation on a stale balance display — worst case the badge updates
          // on the next page load.
        }
        navigate(`/scan/${result.scanId}`);
      } else {
        // Carry the token in the URL hash so the visitor can bookmark or share the link and
        // still see their own scan. The token is also in localStorage as a fallback.
        navigate(`/scan/${result.scanId}?t=${encodeURIComponent(result.accessToken!)}`);
      }
    },
    onError: (err: unknown) => {
      setErrorMessage(null);
      setInsufficientCredits(false);

      if (err instanceof ApiError) {
        if (err.status === 429) {
          setErrorMessage(
            "You've started the maximum number of free scans for this hour. Try again later, or sign up for a paid plan to remove the limit.",
          );
          return;
        }
        if (err.status === 401) {
          // Anonymous path doesn't 401 — this branch only fires when a signed-in user's
          // session expired between page load and submit. Send them through sign-in and
          // bring them back.
          navigate('/sign-in?returnUrl=/');
          return;
        }
        // Backend throws AppException with a message starting with "Insufficient credits"
        // when the user has fewer credits than CreditsPerScan. Surface that as a dedicated UI.
        if (err.status === 422 && /insufficient credits/i.test(err.detail ?? '')) {
          setInsufficientCredits(true);
          return;
        }
        setErrorMessage(err.detail || err.title);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!url.trim()) return;
    mutation.mutate(url.trim());
  };

  const onCtaSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    const target = ctaUrl.trim() || url.trim();
    if (!target) return;
    mutation.mutate(target);
  };

  return (
    <div className="landing">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }}
      />

      {/* ── Split hero ── */}
      <section className="hero">
        <div className="hero-split">
        <div className="hero-left">
          <span className="hero-badge">
            <span className="hero-badge-dot" aria-hidden="true" />
            EU Accessibility Act in force since 28 June 2025
          </span>
          <h1>
            A WCAG audit for sites preparing for the <span className="accent">EU Accessibility Act</span>.
          </h1>
          <p className="lead">
            Drop your URL. Get an automated WCAG 2.1 AA audit and a draft accessibility statement
            in 60 seconds — a starting point for your own compliance review.
          </p>

          <ScanForm
            url={url}
            setUrl={setUrl}
            email={email}
            setEmail={setEmail}
            marketingConsent={marketingConsent}
            setMarketingConsent={setMarketingConsent}
            onSubmit={onSubmit}
            isPending={mutation.isPending}
            isOutOfCredits={isOutOfCredits}
            user={user}
          />

          {/* Free-tier trust row — shown only to signed-out visitors so existing paid users
              don't see "Free" alongside their credit balance, which would be confusing. */}
          {!user && (
            <ul className="free-tier-row" aria-label="Free scan details">
              <li><span aria-hidden="true">✓</span> First scan free</li>
              <li><span aria-hidden="true">✓</span> No signup needed</li>
              <li><span aria-hidden="true">✓</span> PDF report emailed in 60s</li>
            </ul>
          )}

          {isOutOfCredits && !insufficientCredits && (
            <div className="alert alert-warn" role="status">
              <strong>You're out of credits.</strong>
              <span> Buy a credit pack to run a scan.</span>
              <Link to="/pricing" className="cta cta-inline">See pricing</Link>
            </div>
          )}

          {insufficientCredits && (
            <div className="alert alert-warn" role="alert">
              <strong>Not enough credits to run this scan.</strong>
              <span> Each scan costs 1 credit. Top up to continue.</span>
              <Link to="/pricing" className="cta cta-inline">See pricing</Link>
            </div>
          )}

          {errorMessage && <p className="error" role="alert">{errorMessage}</p>}
          {mutation.isPending && <p className="muted">Queueing your scan…</p>}

          <p className="hero-sample-link">
            Curious what you'll get back? <Link to="/sample">See a real sample report →</Link>
          </p>
        </div>

        <div className="hero-right" aria-hidden="true">
          <MockReportPreview />
        </div>
        </div>
      </section>

      <div className="spec-strip" aria-label="Product attributes">
        <ul className="spec-strip-inner" role="list">
          <li className="spec-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
            Powered by axe-core
          </li>
          <li className="spec-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
            EN 301 549 compliant
          </li>
          <li className="spec-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
            WCAG 2.1 AA
          </li>
          <li className="spec-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
            60-second scans
          </li>
          <li className="spec-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
            €29 one-time
          </li>
        </ul>
      </div>

      {/* ── Social proof bar — trust badges + counter ── */}
      <section className="proof-bar" aria-label="Why teams choose EAA Proof">
        <ul className="proof-bar-list">
          <li className="proof-bar-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
            Stripe-secure checkout
          </li>
          <li className="proof-bar-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            EU-hosted (Hetzner + Cloudflare R2)
          </li>
          <li className="proof-bar-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            GDPR-compliant
          </li>
          <li className="proof-bar-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            7-day money-back
          </li>
          <li className="proof-bar-item">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-5"/></svg>
            EU model statement compliant
          </li>
        </ul>
      </section>

      <TechStackBar />

      <section className="trust">
        <div className="trust-grid">
          <div className="trust-card">
            <span className="trust-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </span>
            <h2>WCAG 2.1 AA</h2>
            <p>Powered by axe-core, the open-source accessibility engine trusted by Microsoft, Google, and the BBC.</p>
          </div>
          <div className="trust-card">
            <span className="trust-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
            </span>
            <h2>Plain-English fixes</h2>
            <p>Each violation comes with a 2-sentence explanation any developer can act on. No jargon.</p>
          </div>
          <div className="trust-card">
            <span className="trust-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="8" y1="13" x2="16" y2="13" />
                <line x1="8" y1="17" x2="13" y2="17" />
              </svg>
            </span>
            <h2>EU-model statement</h2>
            <p>A downloadable draft accessibility statement based on the EU model template — adapt it and publish at /accessibility.</p>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="how-it-works" aria-labelledby="hiw-heading">
        <div className="hiw-inner">
          <header className="section-header">
            <h2 id="hiw-heading">From URL to audit report in 60 seconds</h2>
          </header>
          <div className="hiw-steps">
            <div className="hiw-step">
              <div className="hiw-step-badge">01</div>
              <div className="hiw-step-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </div>
              <h3>Drop your URL</h3>
              <p>We crawl your homepage with Playwright + axe-core. No setup, no install, no browser extension.</p>
            </div>
            <div className="hiw-connector" aria-hidden="true" />
            <div className="hiw-step">
              <div className="hiw-step-badge">02</div>
              <div className="hiw-step-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3>AI explains each violation</h3>
              <p>Claude turns WCAG rule codes into plain English with before/after code examples — temperature 0, no hallucinations.</p>
            </div>
            <div className="hiw-connector" aria-hidden="true" />
            <div className="hiw-step">
              <div className="hiw-step-badge">03</div>
              <div className="hiw-step-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="9" y1="13" x2="15" y2="13"/>
                  <line x1="9" y1="17" x2="12" y2="17"/>
                </svg>
              </div>
              <h3>Get audit PDF + statement</h3>
              <p>Download a WCAG 2.1 AA audit PDF and a ready-to-publish EN 301 549 accessibility statement.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why not just… comparison ── */}
      <section className="comparison" aria-labelledby="comparison-heading">
        <div className="comparison-inner">
          <header className="section-header">
            <h2 id="comparison-heading">Why not just hire a consultant or DIY it?</h2>
          </header>
          <div className="comparison-table" role="table" aria-label="Comparison of accessibility audit options">
            <div className="comparison-col" role="rowgroup">
              <div className="comparison-col-head" role="columnheader">
                <span className="comparison-col-label">DIY audit</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Time</span>
                <span className="comparison-row-val" role="cell">Hours of reading WCAG docs</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Cost</span>
                <span className="comparison-row-val" role="cell">Free (your time)</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Defensibility</span>
                <span className="comparison-row-val" role="cell">Uncertain — manual, no audit trail</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Re-scan on change</span>
                <span className="comparison-row-val" role="cell">Manual repeat effort</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Statement included</span>
                <span className="comparison-row-val comparison-no" role="cell" aria-label="No">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  No
                </span>
              </div>
            </div>

            <div className="comparison-col" role="rowgroup">
              <div className="comparison-col-head" role="columnheader">
                <span className="comparison-col-label">Hire a consultant</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Time</span>
                <span className="comparison-row-val" role="cell">2–6 weeks turnaround</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Cost</span>
                <span className="comparison-row-val" role="cell">€1,500–€5,000+</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Defensibility</span>
                <span className="comparison-row-val" role="cell">Strong — expert sign-off</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Re-scan on change</span>
                <span className="comparison-row-val" role="cell">Costly to refresh</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Statement included</span>
                <span className="comparison-row-val comparison-sometimes" role="cell">Sometimes</span>
              </div>
            </div>

            <div className="comparison-col comparison-col-highlight" role="rowgroup">
              <div className="comparison-col-head" role="columnheader">
                <span className="comparison-col-label">EAA Proof</span>
                <span className="comparison-col-badge">Recommended</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Time</span>
                <span className="comparison-row-val comparison-yes" role="cell">60 seconds</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Cost</span>
                <span className="comparison-row-val comparison-yes" role="cell">From €29</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Defensibility</span>
                <span className="comparison-row-val comparison-yes" role="cell">axe-core + EN 301 549 cited</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Re-scan on change</span>
                <span className="comparison-row-val comparison-yes" role="cell">Instant re-scan</span>
              </div>
              <div className="comparison-row" role="row">
                <span className="comparison-row-key" role="rowheader">Statement included</span>
                <span className="comparison-row-val comparison-yes" role="cell" aria-label="Yes">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                  Yes
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Competitor comparison shortcuts ── */}
      <section className="landing-vs" aria-labelledby="landing-vs-heading">
        <div className="landing-vs-inner">
          <h2 id="landing-vs-heading">Considering an overlay or enterprise platform?</h2>
          <p className="muted landing-vs-sub">
            See how EAA Proof compares to the alternatives — pricing, what gets scanned, and
            which one is the right fit for your situation.
          </p>
          <div className="landing-vs-grid">
            <Link to="/vs/accessibe" className="landing-vs-card">
              <span className="landing-vs-card-eyebrow">Comparison</span>
              <h3>EAA Proof vs AccessiBe</h3>
              <p>Why real audits beat the overlay widget approach (and the regulators agree).</p>
            </Link>
            <Link to="/vs/userway" className="landing-vs-card">
              <span className="landing-vs-card-eyebrow">Comparison</span>
              <h3>EAA Proof vs UserWay</h3>
              <p>The free-tier widget vs a proper EN 301 549 statement: which one ships?</p>
            </Link>
            <Link to="/vs/siteimprove" className="landing-vs-card">
              <span className="landing-vs-card-eyebrow">Comparison</span>
              <h3>EAA Proof vs Siteimprove</h3>
              <p>SMB self-serve at €29 vs enterprise platform with a procurement cycle.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Indie-developer trust section — cold-traffic ads land here ── */}
      <section className="founder" aria-labelledby="founder-heading">
        <div className="founder-inner">
          <h2 id="founder-heading">Built by an independent developer who needed it</h2>
          <div className="founder-row">
            <div className="founder-avatar" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
            </div>
            <div className="founder-bio">
              <p>
                EAA Proof is built and run by a single EU-based developer. It started after
                watching small businesses scramble for compliance ahead of the June 2025 EAA
                deadline — with the only options being a $50/month overlay widget regulators
                don't accept, or a five-figure enterprise contract. There had to be a real
                audit between those two extremes. EAA Proof is it.
              </p>
              <p className="muted small">
                Questions? Email{' '}
                <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> — it goes straight
                to the person who writes the code.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="faq" aria-labelledby="faq-heading">
        <div className="faq-inner">
          <header className="faq-head">
            <h2 id="faq-heading">Frequently asked questions</h2>
            <p className="muted">
              The short, honest answers to what people ask us before they buy. Still unsure?{' '}
              <a href="mailto:support@eaaproof.com">Email us</a> — or see the{' '}
              <Link to="/faq">full FAQ</Link>.
            </p>
          </header>
          <div className="faq-list">
            {FAQ_ITEMS.map(({ q, a }) => (
              <details key={q} className="faq-item">
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
          <p className="faq-more">
            <Link to="/faq">See all 15 frequently asked questions →</Link>
          </p>
        </div>
      </section>

      {/* ── Final dark CTA band ── */}
      <section className="landing-cta-band" aria-labelledby="cta-band-heading">
        <div className="landing-cta-band-inner">
          <h2 id="cta-band-heading">Run your first scan in 60 seconds</h2>
          <p className="landing-cta-band-sub">
            No setup, no install. Paste your URL and get back a full WCAG 2.1 AA report with a ready-to-publish accessibility statement.
          </p>
          <ScanForm
            url={ctaUrl}
            setUrl={setCtaUrl}
            email={email}
            setEmail={setEmail}
            marketingConsent={marketingConsent}
            setMarketingConsent={setMarketingConsent}
            onSubmit={onCtaSubmit}
            isPending={mutation.isPending}
            isOutOfCredits={isOutOfCredits}
            user={user}
            dark
          />
          {mutation.isPending && <p className="landing-cta-band-pending">Queueing your scan…</p>}
        </div>
      </section>
    </div>
  );
}
